import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { QueryClient } from '@tanstack/vue-query'

import { queryClient as appQueryClient } from '@/shared/api'
import type { ApiSingleResponse } from '@/shared/api'

import {
  acceptTerms,
  dismissSystemMessage,
  getAccount,
  requestCall,
  requestMarketplace
} from '../api'

import { accountKeys } from './keys'
import type { Account } from './types'

/**
 * What the cache holds: the axios response, envelope and all.
 *
 * Spelled structurally rather than as `AxiosResponse` — the FSD tree names no axios
 * (ESLint `no-restricted-imports`), and the one field anything here reads is `data`.
 */
type AccountResponse = { data: ApiSingleResponse<Account> }

export function readAccount(client: QueryClient = appQueryClient) {
  return (
    client.getQueryData<AccountResponse>(accountKeys.current())?.data.data ??
    null
  )
}

/**
 * Fetch it once and keep it.
 *
 * `fetchQuery` rather than a bare call: it fills the same cache entry `useAccountQuery()`
 * reads, so the guard's request is the only one — the first `useAccount()` to render finds
 * the answer already there.
 */
export async function fetchAccount(client: QueryClient = appQueryClient) {
  const response = await client.fetchQuery({
    queryKey: accountKeys.current(),
    queryFn: ({ signal }) => getAccount({ signal })
  })

  return response.data.data
}

export function useAccountQuery() {
  return useQuery({
    queryKey: accountKeys.current(),
    queryFn: ({ signal }) => getAccount({ signal })
  })
}

function useAccountMutation<TArgs>(
  mutationFn: (args: TArgs) => Promise<AccountResponse>
) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (response) => {
      qc.setQueryData(accountKeys.current(), response)
    }
  })
}

export const useRequestCall = () => useAccountMutation(requestCall)
export const useRequestMarketplace = () =>
  useAccountMutation(requestMarketplace)
export const useAcceptTerms = () => useAccountMutation(acceptTerms)
export const useDismissSystemMessage = () =>
  useAccountMutation(dismissSystemMessage)
